import * as React from 'react'
import { cn } from '@/lib/utils'
import { Icon, type IconName } from '@/design-system/atoms/Icon'

export interface EmptyStateProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title'
> {
  title: React.ReactNode
  description?: React.ReactNode
  iconName?: IconName
  illustration?: React.ReactNode
  action?: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const iconSizeBySize: Record<
  NonNullable<EmptyStateProps['size']>,
  32 | 48 | 64
> = {
  sm: 32,
  md: 48,
  lg: 64,
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      className,
      title,
      description,
      iconName,
      illustration,
      action,
      size = 'md',
      ...rest
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' ? 'py-6' : size === 'lg' ? 'py-16' : 'py-12',
        className
      )}
      {...rest}
    >
      {illustration ? (
        <div className="mb-4">{illustration}</div>
      ) : iconName ? (
        <div className="mb-4 rounded-full bg-gray-100 p-4 dark:bg-gray-800">
          <Icon
            name={iconName}
            size={iconSizeBySize[size]}
            className="text-gray-400"
          />
        </div>
      ) : null}
      <h3
        className={cn(
          'font-semibold text-gray-900 dark:text-white',
          size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-xl' : 'text-base'
        )}
      >
        {title}
      </h3>
      {description && (
        <p
          className={cn(
            'mt-1 max-w-sm text-gray-500 dark:text-gray-400',
            size === 'sm' ? 'text-xs' : 'text-sm'
          )}
        >
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
)

EmptyState.displayName = 'EmptyState'
